// Viewport capture for the feedback dialog.
//
// snapdom clones the live DOM into an SVG snapshot, so it needs two things
// handled here rather than at the call site: the truncation workaround below
// (which is load-bearing, not defensive), and font inlining — an SVG snapshot
// cannot reach the app's stylesheets, so the self-hosted Inter faces must be
// embedded or the capture silently falls back to a system font.

import { snapdom } from '@zumer/snapdom';

// snapdom emulates line-clamp / text-overflow by writing textContent on the LIVE
// element, replacing the text node React holds in its fiber — the next render of
// that node throws NotFoundError from removeChild and unmounts the whole app.
// Neutralize both of its gates for the capture; the pinned height keeps the
// image unchanged. Carried over from Nexus, where this was diagnosed.
function freezeTruncatedText(): () => void {
  const targets: Array<{
    el: HTMLElement;
    prev: string | null;
    clamped: boolean;
    ellipsized: boolean;
    height: number;
  }> = [];

  document.body.querySelectorAll<HTMLElement>('*').forEach((el) => {
    const cs = getComputedStyle(el);
    const clamp = parseInt(
      cs.getPropertyValue('-webkit-line-clamp') || cs.getPropertyValue('line-clamp'),
      10
    );
    const clamped = Number.isFinite(clamp) && clamp > 0;
    const ellipsized = cs.textOverflow === 'ellipsis';
    if (!clamped && !ellipsized) return;
    targets.push({
      el,
      prev: el.getAttribute('style'),
      clamped,
      ellipsized,
      height: clamped ? el.getBoundingClientRect().height : 0,
    });
  });

  targets.forEach(({ el, clamped, ellipsized, height }) => {
    if (clamped) {
      el.style.maxHeight = `${height}px`;
      el.style.setProperty('-webkit-line-clamp', 'none', 'important');
      el.style.setProperty('line-clamp', 'none', 'important');
    }
    if (ellipsized) el.style.setProperty('text-overflow', 'clip', 'important');
  });

  return () =>
    targets.forEach(({ el, prev }) => {
      if (prev === null) el.removeAttribute('style');
      else el.setAttribute('style', prev);
    });
}

// The marker the dialog and its scrim carry so the capture drops them. The
// dialog stays OPEN through the capture (closing it would lose the half-typed
// report), so it has to be excluded from the clone rather than unmounted.
export const CAPTURE_EXCLUDE_ATTR = 'data-feedback-chrome';

export type CapturedImage = {
  filename: string;
  mime_type: string;
  content_base64: string;
  data_url: string;
  size: number;
};

function base64FromDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? '' : dataUrl.slice(comma + 1);
}

function decodedBytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

export async function captureViewport(filename: string): Promise<CapturedImage> {
  const release = freezeTruncatedText();
  try {
    const canvas = await snapdom.toCanvas(document.body, {
      dpr: window.devicePixelRatio || 1,
      scale: 1,
      backgroundColor: '#ffffff',
      clip: 'viewport',
      embedFonts: true,
      excludeMode: 'remove',
      exclude: [`[${CAPTURE_EXCLUDE_ATTR}]`],
    });
    const dataUrl = canvas.toDataURL('image/png');
    const content_base64 = base64FromDataUrl(dataUrl);
    return {
      filename,
      mime_type: 'image/png',
      content_base64,
      data_url: dataUrl,
      size: decodedBytes(content_base64),
    };
  } finally {
    release();
  }
}

export async function readImageFile(file: File): Promise<CapturedImage> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
  const content_base64 = base64FromDataUrl(dataUrl);
  if (!content_base64) throw new Error('Could not read that file.');
  return {
    filename: file.name || 'screenshot.png',
    mime_type: file.type || 'image/png',
    content_base64,
    data_url: dataUrl,
    size: decodedBytes(content_base64),
  };
}
